import { matchesURL } from "@selector/shared";
import { is } from "ramda";
import { checkIsTextOfAFloat32, RXEXP_FLOATINGPOINT_NUMBER_TEXT } from "widgets/utils";

import {
  type Contig,
  type DynamicTerm,
  type Organism,
  type ProjectFile,
  type Region,
  type VocabularyTerm,
} from "../graphql.js";
import { getGroupedNearPathsBeforeLastUnderscore } from "../modules/files/utils/file.js";
import { ATTRIBUTE_FILE_TYPES, MAX_FREE_TEXT_LENGTH, MAX_NUMBER_LENGTH } from "../settings/settings.js";
import { validateRegionInputField } from "../ui-modules/core/redux-form/RegionSelectorField.js";
import { illegalFilenameCharactersRegExp, validFileName } from "./export.js";
import { type ISelectOption, simpleDateValidation, simpleDropdownFieldValidation } from "./form.js";
import { type IConfiguredAttribute } from "./playbook.js";

export const whiteSpaceValidation = (value: string) => {
  const CRLFTAB = "\\r|\\n|\\t|\\f";

  if (!value || value.trim().length === 0) {
    return;
  }

  const crLfTab = new RegExp(CRLFTAB);
  if (value.trim().match(crLfTab)) {
    return `Input contains invalid white space characters like CRs, LFs or Tabs`;
  }
};

export const freeTextValidation = (value: string, attribute: IConfiguredAttribute) => {
  if (!(attribute.mandatory || attribute.correctedMandatory)) {
    if (!value || value.trim().length === 0) {
      return;
    }
  }

  if (!value || value.trim().length === 0) {
    return `Your input is required`;
  }

  const maxLength = Math.min(MAX_FREE_TEXT_LENGTH, attribute.maxLength ? attribute.maxLength : MAX_FREE_TEXT_LENGTH);

  if (value.trim().length > maxLength) {
    return `Maximum length (${maxLength}) exceeded`;
  }

  if (attribute.pattern && attribute.pattern.length > 0) {
    const input = value.trim();
    const regexp = new RegExp(attribute.pattern, "g");

    if (!regexp.test(input)) {
      return attribute.patternHint ? attribute.patternHint : `Invalid format, expected '${attribute.pattern}'`;
    }
  }
};

// always assume the number can be a number or string type ! depends on the input and on the mode.
// the form always provides a stringified value
export const numberValidation = (typedValue: number | string, attribute: IConfiguredAttribute): string => {
  const text: string = is(Number, typedValue) ? typedValue.toString() : !typedValue ? typedValue : typedValue.trim();

  const coreValidation = numberBasicValidation(text, attribute.mandatory || attribute.correctedMandatory);
  if (coreValidation) {
    return coreValidation;
  }

  // Its a 32-bit Float
  //    can be outside allowed min,max-range or it violates the required regex

  if (!!attribute.pattern && attribute.pattern.length > 0) {
    const input = text.trim();
    const regexp = new RegExp(attribute.pattern, "g");

    if (!regexp.test(input)) {
      return attribute.patternHint ? attribute.patternHint : `Invalid format, expected '${attribute.pattern}'`;
    }
  }

  const numberValue = Number.parseFloat(text);

  if (attribute.minValue !== null) {
    if (numberValue < attribute.minValue) {
      return `Value must not be smaller than ${attribute.minValue}`;
    }
  }

  if (attribute.maxValue !== null) {
    if (numberValue > attribute.maxValue) {
      return `Value must not be larger than ${attribute.maxValue}`;
    }
  }

  return null;
};

export const numberBasicValidation = (text: string, mandatory: boolean) => {
  // it is either  number.toString() or string.trim() or undefined or null
  if (text === undefined || text === null || text.length === 0) {
    if (!mandatory) {
      // accept no input for non-mandatory attribute.name
      return;
    } else {
      return `Your input is required`;
    }
  }

  if (text.length > MAX_NUMBER_LENGTH) {
    return `Exceeds the maximum number of ${MAX_NUMBER_LENGTH} characters`;
  }
  // It is either number-text or invalid-text and length > 0 and < 40 chars

  if (!RegExp("^" + RXEXP_FLOATINGPOINT_NUMBER_TEXT + "$").test(text)) {
    // Catches:
    //  'garbage' text with characters outside of [-+0-9e.]: 'as_df', '123,f' '+Inf', 'NaN' '0xff'
    //  Non-parseable number-like garbage text:  '1.e' '.e' 'e1' '++1', '1e1e1' 'e.' '++.+--'
    //  text parsed by parseFloat which is still wrong:
    //    *   "1.e-1.2",  parseFloat converts it to 0.1
    return "Invalid numeric format";
  }

  // It is a number-text
  //   1e39 and 1e-39 are still around
  //   The length of the mantissa is not restricted.
  const numberValue = Number.parseFloat(text);

  if (Number.isNaN(numberValue)) {
    return `Invalid numeric format or not a number`;
  }

  if (!Number.isFinite(numberValue)) {
    return `Infinite values are not allowed`;
  }

  // text contains a finite number.
  //    can still be outside 32-bit Float implementation limits
  //    can be outside allowed min,max-range or violates the required regex
  //    0e999999999999999999999999999999 since parseFloat converts it to 0
  //   -9999999999999999999999999999999999 -> -1e+34
  //   -1e-9999999999999999999999999999999999
  //   0.0008060879772529006  -> 21 chars in mantissa == error
  const notAFloat = checkIsTextOfAFloat32(text);

  if (notAFloat) {
    // Catches:
    //   0e999999999999999999999999999999,  parseFloat converts it to  0
    //   Parse-able number or number-like text
    //   number-text representing numbers outside 32-bit Float range: '1e40',  '3e-38'
    return notAFloat.message;
  }
  return null;
};

export const dateValidation = (value: string, attribute: IConfiguredAttribute) => {
  if (!(attribute.mandatory || attribute.correctedMandatory)) {
    if (!value?.trim()) {
      return;
    }
  }

  return simpleDateValidation(value);
};

export const hyperlinkValidation = (value: string, attribute: IConfiguredAttribute) => {
  if (!(attribute.mandatory || attribute.correctedMandatory)) {
    if (!value || value.trim().length === 0) {
      return;
    }
  }

  if (!value || value.trim().length === 0) {
    return `Your input is required`;
  }

  const maxLength = Math.min(MAX_FREE_TEXT_LENGTH, attribute.maxLength || MAX_FREE_TEXT_LENGTH);

  if (value.trim().length > maxLength) {
    return `Maximum length (${maxLength}) exceeded`;
  }

  if (!matchesURL(value)) {
    return `Invalid URL format`;
  }
};

export const booleanValidation = (value: any, attribute: IConfiguredAttribute) => {
  if (!(attribute.mandatory || attribute.correctedMandatory)) {
    if (!value) {
      return;
    }
  }

  if (!value) {
    return `Your input is required`;
  }

  if (value !== true && value !== false && value.toLowerCase() !== "true" && value.toLowerCase() !== "false") {
    return `Invalid input (when copy pasting, please use TRUE/FALSE values)`;
  }
};

export const fileAttributeValidation = (files: ProjectFile[], attribute: IConfiguredAttribute) => {
  if ((attribute.mandatory || attribute.correctedMandatory) && !files?.length) {
    return "Your input is required";
  }
  if (attribute.fileType.id === ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA) {
    if (Array.isArray(files) && files.length > 1) {
      return "Aligned Read data expects only one single file";
    }
  }
  if (attribute.fileType.id === ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA) {
    if (Array.isArray(files)) {
      if (files.length > 2) {
        return "Unaligned Read data expects 2 files at most.";
      } else if (files.length === 2) {
        const groupedRawDataFiles = getGroupedNearPathsBeforeLastUnderscore(files);
        if (groupedRawDataFiles.length > 1) {
          return "Paired-End Unaligned Read data should match pattern _{1,2} or _R{1,2}";
        }
      }
    }
  }
  if (!attribute.multipleValues && Array.isArray(files) && files.length > 1) {
    return "Only one single file or folder is expected.";
  }
};

export const controlledVocOptionValidation = (option: ISelectOption, attribute: IConfiguredAttribute) => {
  return controlledVocValidation(option?.label, attribute);
};

export const controlledVocValidation = (value: string, attribute: IConfiguredAttribute) => {
  if (!value?.trim()?.length) {
    if (attribute.mandatory || attribute.correctedMandatory) {
      return "Your input is required";
    }
    return;
  }

  const vocabularyTerms: VocabularyTerm[] = attribute.vocabularyTerms || [];
  if (!vocabularyTerms.find((term: VocabularyTerm) => term.name === value)) {
    return "Select a valid value from the list";
  }
};

export const dynamicTermOptionValidation = (option: ISelectOption, attribute: IConfiguredAttribute) => {
  return dynamicTermsValidation(option?.label, attribute);
};

export const dynamicTermsValidation = (value: string, attribute: IConfiguredAttribute) => {
  if (!value?.trim()?.length) {
    if (attribute.mandatory || attribute.correctedMandatory) {
      return "Your input is required";
    }
    return;
  }

  const dynamicTerms: DynamicTerm[] = attribute.dynamicTerms || [];
  if (!dynamicTerms.find((term: DynamicTerm) => term.name === value)) {
    return "Select a valid value from the list";
  }
};

export const fileNameFieldValidation = (value: any) => {
  if (value && !validFileName(value)) {
    const forbiddenChars = value.match(illegalFilenameCharactersRegExp).join("");
    return `The name contains characters that are not allowed: ${forbiddenChars}`;
  }
};

export const filePickerValidation = (value: ProjectFile[], attribute: IConfiguredAttribute) => {
  if (!(attribute.mandatory || attribute.correctedMandatory)) {
    return;
  }
  if (!value?.length) {
    return `Your input is required`;
  }
};

export const refGenomeValidation = (value: ProjectFile[] | Organism, attribute: IConfiguredAttribute) => {
  if (!(attribute.mandatory || attribute.correctedMandatory)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    // case where the value was set by file picker
    if (!value.length) {
      return `Your input is required`;
    }
  } else {
    // case where the value was set by selecting a genome
    if (!value) {
      return `Your input is required`;
    }
  }
  return undefined;
};

export const genomicRegionValidation = (value: any, attribute: IConfiguredAttribute) => {
  const region: Region = value;
  const contig: Contig = region?.contig;

  const contigSimpleValidation = simpleDropdownFieldValidation(contig);

  if (attribute.mandatory || attribute.correctedMandatory) {
    if (contigSimpleValidation) {
      return contigSimpleValidation;
    }
  }
  return validateRegionInputField(value);
};
